import { ReactNode, useCallback, useEffect, useState } from "react";
import { CollaboratorsContext } from "./CollaboratorsContext";
import { useTeam } from "../Team";
import {
  CollaboratorType,
  MultiplayerBasePayloadType,
  UsersLocation,
} from "../../types/Multiplayer.types";
import { useAuth } from "../../common/hooks/useAuth";
import { useMultiplayer } from "../Multiplayer/useMultiplayer";
import { uniqBy, debounce } from "lodash";

interface Props {
  children: ReactNode;
}

export const CollaboratorsProvider = ({ children }: Props) => {
  const [activeCollaborators, setActiveCollaborators] = useState<
    CollaboratorType[]
  >([]);
  const { userInfo } = useAuth();
  const { teammates } = useTeam();
  const { onCollaboratorsLocationUpdate, removeCollaboratorsLocationUpdate } =
    useMultiplayer();

  const mappedCollaborator = useCallback(
    (userLocation: UsersLocation): CollaboratorType | null => {
      const user = teammates?.find((u) => u.id === userLocation.userId);
      if (!user) return null;
      return {
        imageUrl: user?.imageUrl,
        userId: userLocation.userId,
        initials: `${user?.firstName[0].toUpperCase()}${user?.lastName[0].toUpperCase()}`,
        designId: userLocation.designId,
        projectId: userLocation.projectId,
        teamId: userLocation.teamId,
      };
    },
    [teammates]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const addOrUpdateCollaborator = useCallback(
    debounce((usersLocation: UsersLocation[]) => {
      const newList = usersLocation
        .filter((userLocation) => {
          if (
            userLocation.userId === userInfo?.id ||
            userLocation.userId == null ||
            !teammates?.find(
              (u) => u.id === userLocation.userId && userLocation.userId != null
            )
          ) {
            return false;
          }
          return true;
        })
        .sort((a, b) => {
          return b.latestUpdate - a.latestUpdate;
        })
        .map((userLocation) => {
          return mappedCollaborator(userLocation);
        })
        .filter((c): c is CollaboratorType => c !== null);
      setActiveCollaborators(uniqBy(newList, "userId"));
    }, 1000),
    [mappedCollaborator, userInfo?.id, teammates]
  );

  useEffect(() => {
    const handleCollaboratorsLocationUpdate = (
      payload: MultiplayerBasePayloadType
    ) => {
      if (payload.usersLocation) {
        addOrUpdateCollaborator(payload.usersLocation);
      }
    };
    if (userInfo?.id) {
      onCollaboratorsLocationUpdate(handleCollaboratorsLocationUpdate);
    }
    return () => {
      removeCollaboratorsLocationUpdate(handleCollaboratorsLocationUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id, addOrUpdateCollaborator]);

  return (
    <CollaboratorsContext.Provider
      value={{
        activeCollaborators,
        setActiveCollaborators,
        addOrUpdateCollaborator,
      }}
    >
      {children}
    </CollaboratorsContext.Provider>
  );
};
